import { type FC, type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { DropdownList, IconButton, type IDropdownItem, Status } from 'shared/ui'
import { makeTextFieldClear, makeTextFieldElement } from 'shared/ui/TextField'
import { LINK_SMART_INBOXES } from 'shared/constants/links'
import { type DefaultInboxStore } from 'entities/DeviceSettings/store/defaultInboxStore'
import styles from './styles.module.scss'

const ScalerLabel = () => (
  <span className={styles.scalerLabel}>
    Scaler <Status title={'New'} intent={'purple'} emphasis={'high'} size={'small'} />
  </span>
)

const smartOptionTooltips: Record<string, { label: ReactNode; content: ReactNode }> = {
  Scaler: {
    label: (
      <>
        Sends messages from all your inbox numbers to balance the load <br />
        <a href={LINK_SMART_INBOXES} target='_blank' rel='noreferrer'>
          Learn more
        </a>
      </>
    ),
    content: <ScalerLabel />,
  },
  'Local presence': {
    label: (
      <>
        Sends messages from local numbers by geographic proximity <br />
        <a href={LINK_SMART_INBOXES} target='_blank' rel='noreferrer'>
          Learn more
        </a>
      </>
    ),
    content: 'Local presence',
  },
}

const enrichItems = (items: IDropdownItem[]): IDropdownItem[] =>
  items.map((item) => ({
    ...item,
    children: item.children?.map((child) => {
      const smartOption: string | undefined = child.data?.smartOptionLabel
      if (!smartOption) return child
      const tooltip = smartOptionTooltips[smartOption]
      if (!tooltip) return child
      return {
        ...child,
        labelContent: tooltip.content,
        tooltipQuestionProps: {
          margin: '10px',
          width: 155,
          type: 'description',
          placement: 'right',
          label: tooltip.label,
        },
        iconQuestionProps: { fontSize: 13 },
      }
    }),
  }))

type IInboxSelectorContentProps = {
  defaultInboxStore: DefaultInboxStore
  onBack: () => void
}

export const InboxSelectorContent: FC<IInboxSelectorContentProps> = observer(
  ({ defaultInboxStore, onBack }) => {
    const { dropdownItems, loading, loadingMore, search, onSearch, loadMore } = defaultInboxStore

    const handleChange = (item: IDropdownItem) => {
      const inboxId: number = item.data?.inboxId ?? Number(item.id)
      const inboxName: string = item.data?.inboxName ?? item.label
      const number: string | null = item.data?.numberFormatted ?? null
      const numberId: number | null = item.data?.numberId ?? null
      const smartOption: 'scaler' | 'local_presence' | null = item.data?.smartOptionKey ?? null
      const selectedChildId: string | null =
        typeof item.id === 'string'
          ? item.id
          : numberId
          ? `${inboxId}_${numberId}`
          : smartOption
          ? `${inboxId}_${smartOption}`
          : null
      defaultInboxStore.setDefaultInbox(
        inboxId,
        inboxName,
        selectedChildId,
        number,
        numberId,
        smartOption
      )
      onBack()
    }

    return (
      <DropdownList
        items={enrichItems(dropdownItems)}
        onChange={handleChange}
        withSearch
        search={search}
        onSearch={onSearch}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
        maxHeight={220}
        serverSideSearch
        placeholder='Search'
        textFieldProps={{
          className: styles.textInput,
          leftActions: [
            makeTextFieldElement({
              element: (
                <IconButton
                  tag='div'
                  icon={'chevronLeft'}
                  size={'small'}
                  variant={'icon'}
                  color={'secondary'}
                  fontSize={15}
                  onClick={onBack}
                  ariaLabel='Back to settings'
                />
              ),
            }),
          ],
          rightActions: [makeTextFieldClear()],
        }}
      />
    )
  }
)
